import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getOverview, listSignals, listReminders } from '../api/client';
import { SignalCard, ReminderCard, EmptyState } from '../components';
import type { Signal } from '../components/SignalCard';
import type { Reminder } from '../components/ReminderCard';

interface OverviewCounts {
  urgent_items: number;
  new_signals: number;
  pending_confirmations: number;
  today_reminders: number;
  items_for_review: number;
}

interface StatCard {
  key: string;
  label: string;
  icon: string;
  count: number;
  accent?: boolean;
  link: string;
}

export default function Overview() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [counts, setCounts] = useState<OverviewCounts | null>(null);
  const [urgentSignals, setUrgentSignals] = useState<Signal[]>([]);
  const [recentSignals, setRecentSignals] = useState<Signal[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      try {
        const [overviewRes, urgentRes, recentRes, remindersRes] =
          await Promise.allSettled([
            getOverview(),
            listSignals({ urgency: 'critical,high', status: 'new', limit: 5 }),
            listSignals({ limit: 10 }),
            listReminders({ status: 'active' }),
          ]);

        if (cancelled) return;

        if (overviewRes.status === 'fulfilled') {
          setCounts(overviewRes.value.data);
        }
        if (urgentRes.status === 'fulfilled') {
          const data = urgentRes.value.data;
          setUrgentSignals(Array.isArray(data) ? data : data.items ?? []);
        }
        if (recentRes.status === 'fulfilled') {
          const data = recentRes.value.data;
          setRecentSignals(Array.isArray(data) ? data : data.items ?? []);
        }
        if (remindersRes.status === 'fulfilled') {
          const data = remindersRes.value.data;
          setReminders(Array.isArray(data) ? data : data.items ?? []);
        }

      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, []);

  const handleConfirm = (id: string) => navigate(`/signals?highlight=${id}`);
  const handleDismiss = (id: string) => navigate(`/signals?highlight=${id}`);
  const handleExpand = (id: string) => navigate(`/signals?highlight=${id}`);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <span className="ml-3 text-gray-500">{t('common.loading')}</span>
      </div>
    );
  }

  const stats: StatCard[] = [
    {
      key: 'urgent',
      label: t('overview.urgentItems'),
      icon: '🔴',
      count: counts?.urgent_items ?? urgentSignals.length,
      accent: (counts?.urgent_items ?? urgentSignals.length) > 0,
      link: '/signals?urgency=critical,high',
    },
    {
      key: 'new',
      label: t('overview.newSignals'),
      icon: '📡',
      count: counts?.new_signals ?? recentSignals.length,
      link: '/signals?status=new',
    },
    {
      key: 'pending',
      label: t('overview.pendingConfirmations'),
      icon: '⏳',
      count: counts?.pending_confirmations ?? 0,
      link: '/signals?status=awaiting_review',
    },
    {
      key: 'reminders',
      label: t('overview.todayReminders'),
      icon: '🔔',
      count: counts?.today_reminders ?? reminders.length,
      link: '/reminders',
    },
    {
      key: 'review',
      label: t('overview.itemsForReview'),
      icon: '👁️',
      count: counts?.items_for_review ?? 0,
      link: '/review',
    },
  ];

  const hasAnyData =
    urgentSignals.length > 0 ||
    recentSignals.length > 0 ||
    reminders.length > 0;

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('overview.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('overview.recentChanges')}</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {stats.map((stat) => (
          <button
            key={stat.key}
            onClick={() => navigate(stat.link)}
            className={`flex flex-col items-center gap-2 rounded-xl border p-4 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 ${
              stat.accent
                ? 'border-red-300 bg-red-50'
                : 'border-gray-200 bg-white'
            }`}
          >
            <span className="text-2xl">{stat.icon}</span>
            <span
              className={`text-3xl font-bold ${
                stat.accent ? 'text-red-600' : 'text-gray-900'
              }`}
            >
              {stat.count}
            </span>
            <span className="text-xs text-gray-500 text-center leading-tight">
              {stat.label}
            </span>
          </button>
        ))}
      </div>

      {!hasAnyData && (
        <EmptyState
          icon="✅"
          title={t('overview.noUrgentItems')}
          description={t('overview.allClear')}
        />
      )}

      {/* Urgent items */}
      {urgentSignals.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span>🔴</span> {t('overview.urgentItems')}
            </h2>
            <button
              onClick={() => navigate('/signals?urgency=critical,high')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('common.viewAll')} →
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {urgentSignals.slice(0, 5).map((signal) => (
              <SignalCard
                key={signal.id}
                signal={signal}
                compact
                onConfirm={handleConfirm}
                onDismiss={handleDismiss}
                onExpand={handleExpand}
              />
            ))}
          </div>
        </section>
      )}

      {/* Recent signals feed */}
      {recentSignals.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span>📡</span> {t('overview.newSignals')}
            </h2>
            <button
              onClick={() => navigate('/signals')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('common.viewAll')} →
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {recentSignals.slice(0, 10).map((signal) => (
              <SignalCard
                key={signal.id}
                signal={signal}
                onConfirm={handleConfirm}
                onDismiss={handleDismiss}
                onExpand={handleExpand}
              />
            ))}
          </div>
        </section>
      )}

      {/* Today's reminders */}
      {reminders.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span>🔔</span> {t('overview.todayReminders')}
            </h2>
            <button
              onClick={() => navigate('/reminders')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('common.viewAll')} →
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {reminders.map((reminder) => (
              <ReminderCard
                key={reminder.id}
                reminder={reminder}
                onSnooze={() => {}}
                onComplete={() => {}}
                onDismiss={() => {}}
                onReschedule={() => {}}
              />
            ))}
          </div>
        </section>
      )}

    </div>
  );
}
